/**
 * ServiceNeedCardList
 *
 * Manages an array of ServiceNeedFormItems, rendering a ServiceNeedCard for each
 * plus an "Add another service need" button.
 * @agentic connections
 */

import { Stack, Button } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { ServiceNeedCard } from "./ServiceNeedCard";
import {
  createEmptyServiceNeedFormItem,
  type ServiceNeedFormItem,
} from "@benchmark/core/types";

/**
 * Props for ServiceNeedCardList.
 * @agentic connections
 */
export type ServiceNeedCardListProps = {
  items: ServiceNeedFormItem[];
  onChange: (items: ServiceNeedFormItem[]) => void;
  /** When true, highlight invalid required fields on each card. */
  showInlineErrors?: boolean;
};

/**
 * Compute per-card validation errors for required fields.
 * @agentic connections
 */
const getCardErrors = (
  item: ServiceNeedFormItem,
): Record<string, string> | undefined => {
  const errors: Record<string, string> = {};
  if (!item.serviceType) {
    errors.serviceType = "Required";
  }
  if (item.modes.length === 0) {
    errors.modes = "Required";
  }
  return Object.keys(errors).length > 0 ? errors : undefined;
};

/**
 * List of service need cards with add/remove capability.
 * @agentic connections
 */
export const ServiceNeedCardList = ({
  items,
  onChange,
  showInlineErrors = false,
}: ServiceNeedCardListProps) => {
  const usedServiceTypes = items
    .map((item) => item.serviceType)
    .filter(Boolean);

  const handleCardChange = (index: number, updated: ServiceNeedFormItem) => {
    const next = [...items];
    next[index] = updated;
    onChange(next);
  };

  const handleCardRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange([...items, createEmptyServiceNeedFormItem()]);
  };

  return (
    <Stack gap="sm">
      {items.map((item, index) => (
        <ServiceNeedCard
          key={item.id}
          index={index}
          item={item}
          onChange={handleCardChange}
          onRemove={handleCardRemove}
          usedServiceTypes={usedServiceTypes}
          errors={showInlineErrors ? getCardErrors(item) : undefined}
        />
      ))}

      <Button
        variant="subtle"
        leftSection={<IconPlus size={14} />}
        onClick={handleAdd}
        size="sm"
      >
        Add another service need
      </Button>
    </Stack>
  );
};
